import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, ChevronLeft, ChevronRight, FileSearch, Search } from 'lucide-react';
import { buildPageList } from '../../utils/gmpBoard';
import { formatFilingDate } from '../../utils/upcomingIpos';
import { DOC_TYPES, DRHP_STATUS, DRHP_TABS, EXCHANGE_OPTIONS } from '../../utils/drhpTracker';
import CompanyLogo from '../common/CompanyLogo';

// Rows shown per page; the table has no size picker
export const PAGE_SIZE = 10;
const COLUMN_COUNT = 7;

const FIELD_CLASS =
  'px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]';

const Dash = () => <span className="text-slate-300">—</span>;

export const DrhpTable = forwardRef(
  (
    {
      rows,
      tab,
      onTabChange,
      tabCounts,
      query,
      onQueryChange,
      status,
      onStatusChange,
      exchange,
      onExchangeChange,
      page,
      onPageChange,
      compact,
      onCompactChange,
      isLoading,
      emptyMessage,
    },
    ref
  ) => {
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const firstIndex = rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
    const lastIndex = Math.min(currentPage * PAGE_SIZE, rows.length);
    const cellY = compact ? 'py-2' : 'py-3.5';

    return (
      <div ref={ref} className="scroll-mt-20 space-y-3">
        {/* Board tabs + density */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="group" aria-label="Filing board" className="flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80 overflow-x-auto scrollbar-none max-w-full">
            {DRHP_TABS.map((option) => {
              const active = option.key === tab;
              return (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onTabChange(option.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                    active ? 'bg-[#008080] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {option.label} ({tabCounts[option.key] || 0})
                </button>
              );
            })}
          </div>
          <div role="group" aria-label="Row density" className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
            View density:
            {[
              { key: false, label: 'Comfortable' },
              { key: true, label: 'Compact' },
            ].map((option) => (
              <button
                key={option.label}
                type="button"
                aria-pressed={compact === option.key}
                onClick={() => onCompactChange(option.key)}
                className={`px-2 py-1 rounded-md border ${
                  compact === option.key ? 'border-[#008080] bg-teal-50 text-[#006666]' : 'border-slate-200 bg-white text-slate-500 hover:text-slate-800'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px_200px] gap-2">
          <label className="relative block">
            <span className="sr-only">Search filings</span>
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search by company name, CIN, lead manager or sector..."
              className={`${FIELD_CLASS} w-full pl-8`}
            />
          </label>
          <label className="block">
            <span className="sr-only">Filing status</span>
            <select value={status} onChange={(e) => onStatusChange(e.target.value)} className={`${FIELD_CLASS} w-full font-semibold`}>
              <option value="all">All Filing Statuses</option>
              {Object.entries(DRHP_STATUS).map(([key, meta]) => (
                <option key={key} value={key}>
                  {meta.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="sr-only">Stock exchange</span>
            <select value={exchange} onChange={(e) => onExchangeChange(e.target.value)} className={`${FIELD_CLASS} w-full font-semibold`}>
              <option value="all">All Stock Exchanges</option>
              {EXCHANGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>

        <section aria-label="DRHP and prospectus filings" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-slate-100">
            <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-slate-600">
              Active Repository Index
              <span className="px-2 py-0.5 rounded-md bg-slate-900 text-white normal-case tracking-normal text-[10px] font-bold">
                {isLoading ? 'Loading…' : `${pageRows.length} of ${rows.length} filings displayed`}
              </span>
            </p>
            <ul className="flex items-center gap-3 text-[11px] text-slate-500">
              <li className="flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5 text-[#008080]" aria-hidden="true" />
                Financial analysis ready
              </li>
              <li className="flex items-center gap-1">
                <FileSearch className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
                Issue page
              </li>
            </ul>
          </div>

          <div className="overflow-x-auto scrollbar-slim">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <th scope="col" className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[220px]">Issuer Company</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Exchange</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Opening Date</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Document Type</th>
                  <th scope="col" className="py-3 px-3 text-right whitespace-nowrap">Issue Size</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Regulatory Stage</th>
                  <th scope="col" className="py-3 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap">Offer Document</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, idx) => (
                    <tr key={`drhp-skel-${idx}`} className="animate-pulse">
                      <td className="sticky left-0 z-10 bg-white py-3.5 pl-4 sm:pl-5 pr-3">
                        <div className="w-44 h-4 bg-slate-200 rounded" />
                        <div className="mt-1.5 w-28 h-3 bg-slate-100 rounded" />
                      </td>
                      {Array.from({ length: COLUMN_COUNT - 1 }).map((__, col) => (
                        <td key={col} className="py-3.5 px-3">
                          <div className="w-14 h-4 bg-slate-100 rounded" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && pageRows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="py-12 text-center text-sm text-slate-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  pageRows.map((row) => (
                    <tr key={row.id} className="group hover:bg-slate-50/70 transition-colors">
                      <td className={`sticky left-0 z-10 bg-white group-hover:bg-slate-50 ${cellY} pl-4 sm:pl-5 pr-3`}>
                        <div className="flex items-center gap-3">
                          {!compact && <CompanyLogo company={row.company} logoUrl={row.logoUrl} size="sm" maxInitials={1} />}
                          <div className="min-w-0">
                            <Link to={row.detailPath} className="font-bold text-slate-900 hover:text-[#008080] leading-snug">
                              {row.company}
                            </Link>
                            {row.note && (
                              <span className="ml-1.5 px-1.5 py-px rounded bg-amber-50 border border-amber-200 text-[10px] font-bold text-amber-700 whitespace-nowrap">
                                {row.note}
                              </span>
                            )}
                            <p className="text-[11px] text-slate-400 truncate max-w-[260px]">
                              {[row.board === 'sme' ? 'SME' : row.board === 'mainboard' ? 'Mainboard' : null, row.sector, row.cin]
                                .filter(Boolean)
                                .join(' • ') || 'Issuer details awaited'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className={`${cellY} px-3`}>
                        {row.exchanges.length ? (
                          <div className="flex flex-wrap gap-1">
                            {row.exchanges.map((name) => (
                              <span key={name} className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-bold text-slate-600 whitespace-nowrap">
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <Dash />
                        )}
                      </td>
                      <td className={`${cellY} px-3 whitespace-nowrap text-slate-700`}>
                        {row.openOn ? formatFilingDate(row.openOn) : <Dash />}
                      </td>
                      <td className={`${cellY} px-3 whitespace-nowrap`}>
                        <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-extrabold ${DOC_TYPES[row.docType]}`}>{row.docType}</span>
                        {row.documentDate && !compact && (
                          <div className="mt-0.5 text-[10px] text-slate-400">
                            {row.documentDateLabel || 'Filed'} {formatFilingDate(row.documentDate)}
                          </div>
                        )}
                      </td>
                      <td className={`${cellY} px-3 text-right whitespace-nowrap tabular-nums text-slate-700`}>
                        {row.sizeCr ? (
                          `₹${row.sizeCr.toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`
                        ) : row.sizeText ? (
                          <span className="text-[11px] text-slate-500">{row.sizeText}</span>
                        ) : (
                          <Dash />
                        )}
                      </td>
                      <td className={`${cellY} px-3 whitespace-nowrap`}>
                        <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-bold ${DRHP_STATUS[row.status].badge}`}>
                          {DRHP_STATUS[row.status].label}
                        </span>
                        {row.priceBand && !compact && <div className="mt-0.5 text-[10px] text-slate-400">Band {row.priceBand}</div>}
                      </td>
                      <td className={`${cellY} pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap`}>
                        {row.analysisReady ? (
                          <Link
                            to={row.detailPath}
                            title="Financials, risk factors and issue structure extracted from the DRHP"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#008080] hover:bg-[#006666] text-[11px] font-bold text-white"
                          >
                            <BarChart3 className="w-3.5 h-3.5" />
                            Analysis
                          </Link>
                        ) : (
                          <Link
                            to={row.detailPath}
                            title={row.pipelineStatus === 'pending_admin_review' ? 'DRHP analysis is being reviewed' : undefined}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-teal-200 hover:text-[#008080]"
                          >
                            <FileSearch className="w-3.5 h-3.5" />
                            View
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span>{isLoading ? 'Loading filings…' : `Showing ${firstIndex} to ${lastIndex} of ${rows.length} entries`}</span>
            </div>
            {totalPages > 1 && (
              <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="Filings pagination">
                <button
                  type="button"
                  onClick={() => onPageChange(1)}
                  disabled={currentPage <= 1}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  First
                </button>
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                  className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {buildPageList(currentPage, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onPageChange(p)}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-xs font-bold border ${
                        p === currentPage ? 'bg-[#008080] border-[#008080] text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                  className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  Last
                </button>
              </nav>
            )}
          </div>
        </section>
      </div>
    );
  }
);

DrhpTable.displayName = 'DrhpTable';

export default DrhpTable;
